import { Buffer } from "buffer";
import { ModelResolver } from "./index";
import { FunctionResolver } from "../functionResolvers";

export const JSON_LIKE_TYPE_NAME = "DefaultModelResolver__JSONLike";

export class DefaultModelResolverJSONLike {
    __asJson: true = true;

    private readonly data: object;

    constructor(data: object) {
        this.data = data;
    }

    toJSON() {
        return JSON.stringify(this.data);
    }
}

const primitiveBuilder =
    (Type: any, isClass: boolean = true) =>
    (value: any) => {
        if (isClass) {
            return new Type(value).valueOf();
        }
        return Type(value).valueOf();
    };

const primitiveValues = [
    "Boolean",
    "Number",
    "String",
    "BigInt",
    "Symbol",
    "Buffer",
];

const unsupportedTypes = ["Promise", "SharedArrayBuffer"];

const ignoredTypesFromExtendedCopy = ["Function", "Object"];

const ignoredTypes: any = {
    null: null,
    undefined,
};

const genericTypes: Record<string, any> = {
    Boolean: primitiveBuilder(Boolean),
    Number: primitiveBuilder(Number),
    String: primitiveBuilder(String),
    Symbol: primitiveBuilder(Symbol, false),
    BigInt: primitiveBuilder(BigInt, false),
    Array,
    RegExp,
    Error,
    Date,
    Map,
    Set,
    Buffer: Buffer.from,
};

const overloadedTypes: Record<
    string,
    {
        deserialize?: (
            model: any,
            getFunction: FunctionResolver["setTheirs"]
        ) => Promise<any>;
        serialize?: (
            model: any,
            getFunctionId: FunctionResolver["setOurs"]
        ) => Promise<string>;
    }
> = {
    Date: {
        serialize: async (model) => model.toISOString(),
        deserialize: async (model) => new Date(model.value),
    },
    Function: {
        serialize: async (model, getFunctionId) =>
            getFunctionId(model) as Promise<any>,
        deserialize: async (model, getFunction) => getFunction(model.id),
    },
    AsyncFunction: {
        serialize: async (model, getFunctionId) =>
            getFunctionId(model) as Promise<any>,
        deserialize: async (model, getFunction) => getFunction(model.id),
    },
    [JSON_LIKE_TYPE_NAME]: {
        serialize: async (model) => model.toJSON(),
        deserialize: async (model) => JSON.parse(model.value),
    },
};

export default class DefaultModelResolver extends ModelResolver {
    static typeName() {
        return "DefaultModelResolver";
    }

    async serialize(
        affectedModel: any,
        getFunction: FunctionResolver["setOurs"]
    ) {
        // Объект → { type, value, id? }. Функции заменяются на id в пуле.
        const model = affectedModel;
        const returnValue: {
            type?: string;
            value?: any;
            id?: string;
            [x: string]: any;
        } = {};

        if (Object.values(ignoredTypes).indexOf(model) === -1) {
            let typeName = model.constructor?.name || "Object";

            if (typeof model === "object" && model && model.__asJson) {
                typeName = JSON_LIKE_TYPE_NAME;
            }

            returnValue.type = typeName;

            if (unsupportedTypes.indexOf(typeName) !== -1) {
                throw new Error(`Unsupported type: ${typeName}`);
            }

            if (typeName === "Function" || typeName === "AsyncFunction") {
                // Функцию по проводу не шлём — только её id.
                returnValue.id = await getFunction(model);
                returnValue.value = {};
                return returnValue;
            }

            const overloaded = overloadedTypes[typeName];
            if (overloaded && overloaded.serialize) {
                returnValue.value = await overloaded.serialize(
                    model,
                    getFunction
                );
            } else if (primitiveValues.indexOf(typeName) === -1) {
                // Объект/класс: рекурсивно сериализовать свои поля (и прототип, если не generic).
                returnValue.value = {};
                const names = Object.getOwnPropertyNames(model);
                for (let i = 0; i < names.length; i++) {
                    const item = names[i];
                    if (item !== "prototype") {
                        returnValue.value[item] = await this.serialize(
                            model[item],
                            getFunction
                        );
                    }
                }
                if (
                    [
                        ...Object.keys(genericTypes),
                        ...ignoredTypesFromExtendedCopy,
                    ].indexOf(typeName) === -1
                ) {
                    const protoNames = Object.getOwnPropertyNames(
                        Object.getPrototypeOf(model)
                    );
                    for (let i = 0; i < protoNames.length; i++) {
                        const item = protoNames[i];
                        if (item !== "constructor") {
                            returnValue.value[item] = await this.serialize(
                                model[item],
                                getFunction
                            );
                        }
                    }
                }
            } else {
                returnValue.value = model;
            }
        } else {
            returnValue.type = `${model}`;
            returnValue.value = model;
        }
        return returnValue;
    }

    async deserialize(
        model: { type?: string; value?: any; id?: string },
        getFunction: FunctionResolver["setTheirs"]
    ) {
        // { type, value, id } → значение; Function id → прокси на удалённый вызов.
        const typeName = model.type || "undefined";

        if (Object.keys(ignoredTypes).indexOf(typeName) !== -1) {
            return model.value;
        }

        const overloaded = overloadedTypes[typeName];
        if (overloaded && overloaded.deserialize) {
            return overloaded.deserialize(model, getFunction);
        }

        if (primitiveValues.indexOf(typeName) !== -1) {
            return genericTypes[typeName](model.value);
        }

        let genericCreature: any;

        if (typeName === "Object") {
            genericCreature = {};
        } else if (Object.keys(genericTypes).indexOf(typeName) !== -1) {
            genericCreature = new genericTypes[typeName]();
        } else {
            const DynamicClass = class {};
            Object.defineProperty(DynamicClass, "name", { value: typeName });
            genericCreature = new DynamicClass();
        }
        const keys = Object.keys(model.value || {});
        for (let i = 0; i < keys.length; i++) {
            const item = keys[i];
            genericCreature[item] = await this.deserialize(
                model.value[item],
                getFunction
            );
        }
        return genericCreature;
    }

    static JSONLike(data: object): DefaultModelResolverJSONLike {
        return new DefaultModelResolverJSONLike(data);
    }
}
